export type DraftLotteryStage = "completed" | "projected" | "ready" | "running";

type DraftLotteryDecisionRow = {
  chances: number;
  pick?: number;
  tid: number;
};

export const getDraftLotteryStage = ({
  hasResult,
  revealState,
  type,
}: {
  hasResult: boolean;
  revealState?: string;
  type: string;
}): DraftLotteryStage => {
  if (type === "projected") {
    return "projected";
  }
  if (revealState && revealState !== "init") {
    return "running";
  }
  if (type === "readyToRun") {
    return "ready";
  }
  if (hasResult) {
    return "completed";
  }
  return "ready";
};

export const getDraftLotteryDecisionContext = ({
  probs,
  result,
  stage,
  userTid,
}: {
  probs?: (number | undefined)[][];
  result: DraftLotteryDecisionRow[] | undefined;
  stage: DraftLotteryStage;
  userTid: number;
}) => {
  const userIndex = result?.findIndex((row) => row.tid === userTid) ?? -1;
  const userRow = userIndex >= 0 ? result?.[userIndex] : undefined;
  const userProbs = userIndex >= 0 ? probs?.[userIndex] : undefined;
  const availablePickOdds =
    userProbs
      ?.map((prob, index) => ({
        pick: index + 1,
        prob,
      }))
      .filter(
        (item): item is { pick: number; prob: number } => item.prob !== undefined && item.prob > 0,
      ) ?? [];
  const expectedPick =
    availablePickOdds.length > 0
      ? availablePickOdds.reduce((sum, item) => sum + item.pick * item.prob, 0)
      : undefined;
  const totalChances = result?.reduce((sum, row) => sum + row.chances, 0) ?? 0;

  return {
    bestPick: availablePickOdds[0]?.pick,
    expectedPick: expectedPick !== undefined ? Number(expectedPick.toFixed(1)) : undefined,
    finalPick: userRow?.pick,
    hasUserPick: userRow !== undefined,
    stage,
    topPickProbability:
      userProbs?.[0] !== undefined ? Number((userProbs[0] * 100).toFixed(1)) : undefined,
    userChances: userRow?.chances,
    userChanceShare:
      userRow && totalChances > 0
        ? Number(((userRow.chances / totalChances) * 100).toFixed(1))
        : undefined,
    worstPick: availablePickOdds.at(-1)?.pick,
  };
};

export type DraftLotteryLuckTone = "better" | "neutral" | "worse";

export const getDraftLotteryLuckDelta = ({
  expectedPick,
  finalPick,
}: {
  expectedPick?: number;
  finalPick?: number;
}) => {
  if (expectedPick === undefined || finalPick === undefined) {
    return undefined;
  }

  const delta = Number((finalPick - expectedPick).toFixed(1));
  const tone: DraftLotteryLuckTone =
    delta < -0.1 ? "better" : delta > 0.1 ? "worse" : "neutral";

  return {
    delta,
    magnitude: Math.abs(delta),
    tone,
  };
};

export const getDraftScoutingPreview = ({
  playerCount,
  previewLimit = 8,
  season,
}: {
  playerCount: number;
  previewLimit?: number;
  season: number | undefined;
}) => ({
  previewCount: Math.min(playerCount, previewLimit),
  previewLimit,
  season,
  segments: ["recommended", "shortlist", "all"] as const,
});

export type DraftScoutingConfidence = "high" | "low" | "medium" | "none";
export type DraftScoutingSeasonMode = "history" | "planning";

export const getDraftScoutingSeasonMode = ({
  requestedSeason,
  firstPlanningSeason,
}: {
  firstPlanningSeason: number;
  requestedSeason?: number;
}): DraftScoutingSeasonMode =>
  requestedSeason !== undefined && requestedSeason < firstPlanningSeason ? "history" : "planning";

export const getDraftScoutingDecisionContext = ({
  playerCount,
  previewLimit = 8,
  season,
}: {
  playerCount: number;
  previewLimit?: number;
  season: number | undefined;
}) => {
  const preview = getDraftScoutingPreview({ playerCount, previewLimit, season });
  const confidence: DraftScoutingConfidence =
    playerCount === 0
      ? "none"
      : preview.previewCount >= preview.previewLimit
        ? "high"
        : preview.previewCount >= 4
          ? "medium"
          : "low";

  return {
    ...preview,
    confidence,
    recommendedCount: Math.min(3, preview.previewCount),
  };
};

export type ProtectionRisk = "blocked" | "complete" | "incomplete" | "readonly";

export const getProtectionStage = ({
  canManageProtections,
  expansionTeam,
  numRemaining,
}: {
  canManageProtections: boolean;
  expansionTeam: boolean;
  numRemaining: number;
}): ProtectionRisk => {
  if (expansionTeam || !canManageProtections) {
    return "readonly";
  }
  if (numRemaining < 0) {
    return "blocked";
  }
  if (numRemaining > 0) {
    return "incomplete";
  }
  return "complete";
};
